import { FC } from 'react';
import { Pencil, Trash2, BarChart2, Building2, Loader2 } from 'lucide-react';
import { PortfolioProperty } from '@/types/index';

interface Props {
  properties: PortfolioProperty[];
  onEdit: (p: PortfolioProperty) => void;
  onDelete: (p: PortfolioProperty) => void;
  onAnalyze?: (propertyId: string) => void;
  isLoading?: boolean;
}

const fmt = (v: number | null | undefined, decimals = 0): string => {
  if (v == null) return '—';
  return new Intl.NumberFormat('fr-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(v);
};

const fmtChf = (v: number | null | undefined): string =>
  v == null ? '—' : `CHF ${fmt(v)}`;

const fmtPct = (v: number | null | undefined): string =>
  v == null ? '—' : `${fmt(v, 1)} %`;

const CashFlowBadge: FC<{ value: number }> = ({ value }) => {
  const color = value >= 0
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {fmtChf(value)}
    </span>
  );
};

const LTVBadge: FC<{ ltv: number | null }> = ({ ltv }) => {
  if (ltv == null) return <span className="text-gray-400">—</span>;
  const pct = ltv * 100;
  const color = pct > 80
    ? 'text-red-600 dark:text-red-400'
    : pct > 65
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-emerald-600 dark:text-emerald-400';
  return <span className={`font-medium ${color}`}>{fmtPct(pct)}</span>;
};

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  residential: 'Résidentiel',
  commercial: 'Commercial',
  mixed: 'Mixte',
};

export const PortfolioTable: FC<Props> = ({ properties, onEdit, onDelete, onAnalyze, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="animate-spin w-6 h-6 mr-2" />
        Chargement...
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
        <Building2 className="w-12 h-12 opacity-40" />
        <p className="text-sm">Aucun actif dans le portefeuille.</p>
        <p className="text-xs">Ajoutez votre premier bien en cliquant sur "+ Ajouter un actif".</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Gradient droit — indique qu'il y a du contenu à scroller (mobile) */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 rounded-r-xl sm:hidden" />
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm min-w-[800px]">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Nom</th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ville</th>
            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Type</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Valeur marché</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dette</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">NOI / an</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cash-flow / an</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">LTV</th>
            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cap rate</th>
            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide min-w-[110px] sticky right-0 bg-gray-50 dark:bg-gray-800 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">Actions</th>
          </tr>
        </thead>
        <tbody>
          {properties.map(p => (
            <tr key={p.id} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-700">
              <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100 max-w-[160px] truncate">
                {p.name}
              </td>
              <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                {p.city ?? '—'}
                {p.canton && <span className="ml-1 text-xs text-gray-400">({p.canton})</span>}
              </td>
              <td className="px-3 py-3 text-gray-600 dark:text-gray-400">
                {PROPERTY_TYPE_LABELS[p.propertyType] ?? p.propertyType}
              </td>
              <td className="px-3 py-3 text-right font-mono text-gray-900 dark:text-gray-100">
                {fmtChf(p.currentMarketValue)}
              </td>
              <td className="px-3 py-3 text-right font-mono text-gray-700 dark:text-gray-300">
                {fmtChf(p.currentDebt)}
              </td>
              <td className="px-3 py-3 text-right font-mono text-gray-700 dark:text-gray-300">
                {fmtChf(p.indicators.NOI)}
              </td>
              <td className="px-3 py-3 text-right">
                <CashFlowBadge value={p.indicators.cashFlow} />
              </td>
              <td className="px-3 py-3 text-right">
                <LTVBadge ltv={p.indicators.LTV} />
              </td>
              <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300">
                {fmtPct(p.indicators.capRate)}
              </td>
              <td className="px-3 py-3 text-center whitespace-nowrap sticky right-0 bg-white dark:bg-gray-900 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-center gap-1.5">
                  {p.propertyId && onAnalyze && (
                    <button
                      onClick={() => onAnalyze(p.propertyId!)}
                      className="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-800/50 transition-colors"
                      title="Analyse & documents PPE"
                      aria-label="Analyse"
                    >
                      <BarChart2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => onEdit(p)}
                    className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Modifier"
                    aria-label="Modifier"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(p)}
                    className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Supprimer"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};
